import { computed } from 'vue';
import { defineStore } from 'pinia';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
import { formatLength } from '@/util/format/formatLength';
import {
    type LtResectionResult,
    resolveLtResection,
} from '@/views/3d-planner/measurements/ltResectionDistance';
import {
    isLtResectionBelowMinimum,
    MIN_LT_RESECTION_DISTANCE_MM,
} from '@/views/3d-planner/measurements/ltResectionWarning';

export const useResectionHeightViewModel = defineStore('resection-height-view', () => {
    const planner = usePlannerStore();
    const scene = usePlannerSceneViewModel();

    /** The resolved LT-resection measurement — owned here, consumed by both the overlay and the
     *  3D scene (a view) via `updateLtResectionVisualisation`. */
    const result = computed<LtResectionResult | null>(() =>
        resolveLtResection({
            fittedStem: planner.fittedStem,
            stemTransform: planner.template?.stemTransform ?? null,
            femurGeometry: scene.nodes.operativeFemur.geometry,
            ltResectionPoint: planner.ltResectionPoint,
            femurShaftAxisDirection: planner.femoralFeatures?.shaftAxis.value ?? null,
        }),
    );

    // Mirrors `result`, not `fittedStem` — a narrower check missed permanent-null cases like a
    // dual-mobility fit with no bearing (see resolveLtResection).
    const visible = computed(() => planner.ltResectionPoint !== null && result.value !== null);

    const distance = computed(() => result.value?.distance ?? null);

    const formattedDistance = computed(() => formatLength(distance.value));

    const warning = computed(
        () => distance.value !== null && isLtResectionBelowMinimum(distance.value),
    );

    // Exposed so the View can compose the warning copy without importing the domain constant
    // directly (keeps the View → ViewModel boundary; the View still owns the copy string).
    return {
        result,
        visible,
        formattedDistance,
        warning,
        warningThresholdMm: MIN_LT_RESECTION_DISTANCE_MM,
    };
});
